import { useAuth } from '@/features/auth';
import { MainLayout } from '@/components/layout';
import { PageHeader } from '@/components/shared';
import { StatsCardSkeleton, ActivityListSkeleton } from '@/components/shared/ListSkeletons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { GraduationCap, BookOpen, CheckCircle, Clock, Play, Eye, Award } from 'lucide-react';
import { useMyProgress } from '@/features/dashboard';
import { formatDistanceToNow } from 'date-fns';

const actionLabels = {
  started: 'Started',
  video_watched: 'Watched video',
  quiz_passed: 'Passed quiz',
  completed: 'Completed',
};

const actionIcons = {
  started: Play,
  video_watched: Eye,
  quiz_passed: Award,
  completed: CheckCircle,
};

export default function Dashboard() {
  const { user } = useAuth();
  const { data: progress, isLoading } = useMyProgress();

  const firstName = user?.profile?.full_name?.split(' ')[0] || 'there';

  const stats = [
    {
      title: 'Modules Completed',
      value: progress?.modulesCompleted || 0,
      total: progress?.totalModules || 0,
      icon: BookOpen,
      description: 'Training modules',
    },
    {
      title: 'Lessons Completed',
      value: progress?.lessonsCompleted || 0,
      total: progress?.totalLessons || 0,
      icon: GraduationCap,
      description: 'Individual lessons',
    },
    {
      title: 'Quizzes Passed',
      value: progress?.quizzesPassed || 0,
      total: progress?.totalLessons || 0,
      icon: CheckCircle,
      description: 'Assessment quizzes',
    },
    {
      title: 'Time Spent',
      value: progress?.timeSpentMinutes 
        ? progress.timeSpentMinutes >= 60 
          ? `${Math.floor(progress.timeSpentMinutes / 60)}h ${progress.timeSpentMinutes % 60}m`
          : `${progress.timeSpentMinutes}m`
        : '0m',
      icon: Clock,
      description: 'Total training time',
    },
  ];

  const progressPercentage = progress?.totalLessons 
    ? Math.round((progress.lessonsCompleted / progress.totalLessons) * 100) 
    : 0;

  if (isLoading) {
    return (
      <MainLayout>
        <PageHeader
          title={`Welcome back, ${firstName}`}
          description="Track your compliance training progress"
        />
        <StatsCardSkeleton count={4} />
        <div className="mt-8 grid gap-4 lg:grid-cols-2 overflow-hidden">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-40" />
              <Skeleton className="h-4 w-60" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="h-2 w-full" />
              <Skeleton className="h-4 w-48" />
            </CardContent>
          </Card>
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-4 w-48" />
            </CardHeader>
            <CardContent>
              <ActivityListSkeleton count={4} />
            </CardContent>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <PageHeader
        title={`Welcome back, ${firstName}`}
        description="Track your compliance training progress"
      />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 overflow-hidden">
        {stats.map((stat) => (
          <Card key={stat.title}>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {stat.title}
              </CardTitle>
              <stat.icon className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stat.value}
                {stat.total !== undefined && typeof stat.value === 'number' && (
                  <span className="text-sm font-normal text-muted-foreground">
                    /{stat.total}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{stat.description}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-8 grid gap-4 lg:grid-cols-2 overflow-hidden">
        <Card>
          <CardHeader>
            <CardTitle>Overall Progress</CardTitle>
            <CardDescription>Your compliance training completion</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 min-w-0">
            <div className="space-y-2">
              <div className="flex flex-wrap justify-between gap-x-2 text-sm">
                <span className="whitespace-nowrap">{progressPercentage}% Complete</span>
                <span className="whitespace-nowrap">{progress?.lessonsCompleted || 0} of {progress?.totalLessons || 0} lessons</span>
              </div>
              <Progress value={progressPercentage} className="h-2" />
            </div>
            {progress?.totalLessons === 0 ? (
              <p className="text-sm text-muted-foreground">
                No training modules are available yet. Check back soon!
              </p>
            ) : progressPercentage === 100 ? (
              <p className="text-sm text-emerald-600">
                Congratulations! You've completed all available training.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Keep going! You're making great progress.
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
            <CardDescription>Your latest training sessions</CardDescription>
          </CardHeader>
          <CardContent>
            {progress?.recentActivity && progress.recentActivity.length > 0 ? (
              <div className="space-y-4">
                {progress.recentActivity.map((activity, index) => {
                  const Icon = actionIcons[activity.action];
                  return (
                    <div key={index} className="flex items-start gap-2">
                      <div className="rounded-full bg-primary/10 p-2 shrink-0">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium break-words">
                          {actionLabels[activity.action]} "{activity.lessonTitle}"
                        </p>
                        <p className="text-xs text-muted-foreground break-words">
                          {activity.moduleName} • {formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                No recent activity. Start a module to see progress here.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
}
